/**
 * Cookie / analytics consent state.
 *
 * Analytics (GA4 and persistent localStorage identifiers) stays off until the
 * visitor opts in. A Global Privacy Control signal counts as a decline until the
 * visitor explicitly opts in from Cookie preferences.
 */

export const CONSENT_STORAGE_KEY = 'bpa_cookie_consent';
/** Bump when the categories or vendors change so visitors are asked again. */
export const CONSENT_VERSION = 1;
/** Re-ask after 12 months. */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

export const OPEN_COOKIE_PREFERENCES_EVENT = 'bpa:open-cookie-preferences';

const listeners = new Set();

function getLocalStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
}

export function hasGlobalPrivacyControl() {
  return typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true;
}

/** Stored decision, or null when the visitor has not decided (or it expired). */
export function readConsent() {
  const storage = getLocalStorage();
  if (!storage) return null;
  try {
    const parsed = JSON.parse(storage.getItem(CONSENT_STORAGE_KEY) || 'null');
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    const decidedAt = Date.parse(parsed.decided_at);
    if (!Number.isFinite(decidedAt) || Date.now() - decidedAt > CONSENT_MAX_AGE_MS) return null;
    return { analytics: parsed.analytics === true, decided_at: parsed.decided_at };
  } catch (_error) {
    return null;
  }
}

export function hasAnalyticsConsent() {
  const consent = readConsent();
  return Boolean(consent && consent.analytics);
}

/**
 * How GA4 may run: 'granted' (opted in: cookies and identifiers), 'cookieless'
 * (no decision yet: Consent Mode pings with analytics_storage denied, so no
 * cookies or stored identifiers) or 'off' (declined, or a GPC signal without
 * an explicit opt-in).
 */
export function analyticsMode() {
  const consent = readConsent();
  if (consent) return consent.analytics ? 'granted' : 'off';
  return hasGlobalPrivacyControl() ? 'off' : 'cookieless';
}

/** True when the banner should be shown (no decision yet and no GPC signal). */
export function needsConsentDecision() {
  return !readConsent() && !hasGlobalPrivacyControl();
}

export function saveConsent({ analytics }) {
  const record = {
    version: CONSENT_VERSION,
    analytics: analytics === true,
    decided_at: new Date().toISOString(),
  };
  const storage = getLocalStorage();
  if (storage) {
    try {
      storage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch (_error) {
      // Storage blocked: the choice still applies for this page load.
    }
  }
  listeners.forEach((listener) => listener(record));
  return record;
}

export function onConsentChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(OPEN_COOKIE_PREFERENCES_EVENT));
}
